import {
  useCallback,
  useState,
  useErrorBoundary,
  useEffect,
  useMemo,
  useRef,
} from "preact/hooks";
import Splash from "./features/splash";
import { FadeTransition } from "./components/Transitions";
import ErrorPage from "./features/error";
import { useAppState } from "./hooks/useAppState";
import { AppState } from "./context/AppStateContext";
import { useRenderer } from "./hooks/useRenderer";
import { Spinner } from "./components/Spinner";
import { useArtwork } from "./hooks/useArtwork";
import { ArtworkState } from "./context/ArtworkContext";
import { OnboardingModals } from "./features/onboarding";
import { useUserForm } from "./hooks/useUserForm";
import useUserDevice from "./hooks/useUserDevice";
import LandscapeOverlay from "./features/landscape-overlay";
import useScreenOrientation from "./hooks/useScreenOrientation";
import useAnalytics from "./hooks/useAnalytics";
import { ImageTrackingOverlay } from "./features/tracking-overlay/ImageTrackingOverlay";
import InfoButton from "./components/InfoButton";
import TrackingOverlay from "./features/tracking-overlay";
import { ArtworkData, artworkData } from "./renderer/artworks";
import BottomBar from "./assets/bottom-bar-lg.png";
import clsx from "clsx";
import RepositionButton from "./components/RepositionButton";
import { CubeSvg } from "./components/CubeSvg";
import { UIContentState } from "./renderer/image-targets";
import { useMount } from "./hooks/useMount";
import { Modal } from "./components/Modal/Modal";
import { ModalOverlay } from "./components/Modal/ModalOverlay";
import bgOnboarding from "./assets/bg-modal-1.png";

const params = new URLSearchParams(window.location.search);
const noAR = params.get("noAR") === "1";
const atHome = params.get("home") === "1";

enum CubeType {
  CAR,
  HOME,
  LANDLORD,
}

enum CubeInfoState {
  SELECT,
  INFO,
  BACK,
}

export function App() {
  // const [hasViewedOnboarding, setHasViewedOnboarding] = useLocalStorageState(
  //   "hasViewedOnboarding",
  //   { defaultValue: false }
  // );

  const { appState, setAppState } = useAppState();
  const { renderer, initExperience, loadArtwork, uiState } = useRenderer();
  const {
    artworkState,
    setArtworkState,
    setCurrentArtwork,
    viewedArtworks,
    currentArtwork,
  } = useArtwork();

  const { isMobile } = useUserDevice();
  const screenOrientation = useScreenOrientation();
  const { userId } = useUserForm();
  const { initAnalytics, sendEvent } = useAnalytics(userId);

  useMount(() => {
    async function clearCacheOnStartup() {
      await caches.delete("json-cache");
    }
    clearCacheOnStartup();
  });

  useEffect(() => {
    // if (import.meta.env.PROD)
    initAnalytics("G-GKGRYXS35T");
  }, [initAnalytics]);

  const canResumeAudio = useRef(false);

  useEffect(() => {
    const handleVisiblityChange = () => {
      if (document.visibilityState === "visible") {
        if (canResumeAudio.current) renderer?.resumeAudio();
      } else {
        renderer?.pauseAudio();
        canResumeAudio.current = true;
        storyDurationStart.current = 0;
      }
    };

    window.addEventListener("visibilitychange", handleVisiblityChange);
    return () => {
      window.removeEventListener("visibilitychange", handleVisiblityChange);
    };
  }, [renderer, artworkState, viewedArtworks]);

  const [loadingArtwork, setLoadingArtwork] = useState(false);
  const handleLoadArtwork = useCallback(
    async (artworkId: number) => {
      if (loadingArtwork) {
        console.warn("An artwork is already being loaded.");
        return;
      }
      // setArtworkState(ArtworkState.LOADING);
      setLoadingArtwork(true);
      try {
        await loadArtwork(artworkId);
        setCurrentArtwork(artworkId);
        setArtworkState(ArtworkState.VIEWING);

        canResumeAudio.current = false;

        // sendEvent('artwork_viewed', {
        //   artwork_id: artworkId,
        // })
      } catch (error) {
        console.error("Failed to load artwork:", error);
      } finally {
        setLoadingArtwork(false);
        setArtworkState(ArtworkState.VIEWING);
      }
    },
    [loadArtwork, loadingArtwork, setCurrentArtwork, setArtworkState]
  );

  const handleInitExperience = useCallback(async () => {
    try {
      await initExperience();
      setAppState(noAR ? AppState.ARTWORK_VIEWING : AppState.ONBOARDING);
      if (noAR) {
        if (artworkState === ArtworkState.NONE)
          setArtworkState(ArtworkState.PLACING);
      }
    } catch (error) {
      console.error("Failed to initialize experience:", error);
    }
  }, [initExperience, setAppState, artworkState, setArtworkState]);

  const shouldRendererPause = useMemo(() => {
    return (
      appState === AppState.ONBOARDING ||
      (screenOrientation?.includes("landscape") && isMobile)
    );
  }, [appState, screenOrientation, isMobile]);

  useEffect(() => {
    return shouldRendererPause
      ? renderer?.pauseTracking()
      : renderer?.resumeTracking();
  }, [shouldRendererPause, renderer]);

  const handleOnboardingClose = () => {
    // setHasViewedOnboarding(true);
    setAppState(AppState.ARTWORK_VIEWING);
    if (artworkState === ArtworkState.NONE)
      setArtworkState(ArtworkState.PLACING);
  };

  useEffect(() => {
    if (artworkState === ArtworkState.VIEWING) renderer?.resumeTracking();
  }, [artworkState, renderer]);

  const [cubeInfoState, setCubeInfoState] = useState(CubeInfoState.SELECT);
  const [cubeType, setCubeType] = useState(CubeType.CAR);
  const storyDurationStart = useRef(0);

  const handleCubeSelect = (cubeId: number) => {
    storyDurationStart.current = Date.now();
    // sendEvent("story_selected", {
    //   story_id: selectedCube,
    // });
    setArtworkState(ArtworkState.LOADING);
    renderer?.selectContent(cubeId);
    setCubeInfoState(CubeInfoState.INFO);
    setCubeType(cubeId);
  };

  const handleBackSelect = () => {
    renderer?.animateContentOut();
    setCubeInfoState(CubeInfoState.SELECT);
    if (storyDurationStart.current) {
      const timeElapsed = Date.now() - storyDurationStart.current;
      sendEvent("story_duration", {
        story_id: CubeType[cubeType],
        time_elapsed_ms: timeElapsed,
      });
      storyDurationStart.current = 0;
    }
  };

  useEffect(() => {
    if (!renderer) return;

    const handleArtworkState = (state: ArtworkState) => {
      setArtworkState(state);
      if (state === ArtworkState.PLACING) {
        // setCubeInfoState(CubeInfoState.SELECT);
      }
    };

    const handleArtworkChange = (artwork: ArtworkData) => {
      if (artworkData[1].id === currentArtwork) return;
      setCurrentArtwork(artworkData[1].id);
      handleLoadArtwork(artworkData[1].id);
    };

    const handleContentLoaded = () => {
      setArtworkState(ArtworkState.VIEWING);
    };

    renderer.on("content-loaded", handleContentLoaded);
    renderer.on("artwork-state", handleArtworkState);
    renderer.on("on-artwork-change", handleArtworkChange);
    return () => {
      renderer.off("artwork-state", handleArtworkState);
      renderer.off("on-artwork-change", handleArtworkChange);
      renderer.off("content-loaded", handleContentLoaded);
    };
  }, [
    renderer,
    setArtworkState,
    handleLoadArtwork,
    currentArtwork,
    cubeInfoState,
    setCurrentArtwork,
  ]);

  const [placegroundMessage, setPlaceGroundMessage] = useState(
    "Move your device to <br /> detect the floor"
  );
  const handleOnStatusNormal = () => {
    setPlaceGroundMessage("Tap on the ground to <br /> position the cube");
  };
  const [showFirstSlideOnboardingOnly, setShowFirstSlideOnboardingOnly] =
    useState(false);

  const handleInfoOpen = () => {
    setAppState(AppState.ONBOARDING);
    setShowFirstSlideOnboardingOnly(true);
  };

  // only show loader if its taking longer than 0.5s
  const [delayedLoading, setDelayedLoading] = useState(false);
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null;
    if (artworkState === ArtworkState.LOADING) {
      timer = setTimeout(() => {
        setDelayedLoading(true);
      }, 500);
    } else {
      setDelayedLoading(false);
      if (timer) clearTimeout(timer);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [artworkState]);

  const [error] = useErrorBoundary();

  const [hasAcknowledged, setHasAcknowledged] = useState(false);

  /**
   * JSX
   */
  if (error) {
    return <ErrorPage error={error} />;
  }
  return (
    <div className="w-full h-[100dvh] h-full flex items-center justify-center ">
      {/* <UserForm /> */}
      <FadeTransition
        show={screenOrientation?.includes("landscape") && isMobile}
        duration={500}
      >
        <div className="h-full w-full ">
          <LandscapeOverlay className="flex lg:hidden" />
        </div>
      </FadeTransition>
      
      <FadeTransition show={appState === AppState.SPLASH}>
        <div className="h-full w-full fixed inset-0">
          <Splash onPermissionsGranted={handleInitExperience} />
        </div>
      </FadeTransition>

      <FadeTransition
        show={appState === AppState.ONBOARDING}
        duration={500}
      >
        <div className="h-full w-full">
          {!hasAcknowledged ? (
            <ModalOverlay>
              <Modal
                maxWidth="max-w-[350px]"
                maxHeight="max-h-[540px]"
                className="bg-cover text-black centered w-[92%] overflow-hidden bg-white animate-fade-in"
              >
                <div
                  className="flex flex-col h-full w-full bg-cover pt-[25px] px-[30px] pb-[30px] bg-top"
                  style={{
                    backgroundImage: `url(${bgOnboarding})`,
                  }}
                >
                  <div className="flex flex-col gap-[8px] text-black flex-shrink-0">
                    <div
                      className={clsx(
                        "flex flex-col justify-end gap-[16px] xpb-[18px] w-full animate-fade-in xopacity-0"
                      )}
                    >
                      <h2 className=" font-cba-sans-bold text-[28px] pb-2">
                        Important Notice
                      </h2>
                    </div>
                  </div>
                  <div className="relative flex-grow overflow-hidden min-h-0">
                    <div className="h-full overflow-y-auto pr-2 pointer-events-auto">
                      <p className="font-cba-sans text-[17px] leading-[1.2rem] pb-[20px]">
                        This product is provided by Hollard Insurance Partners
                        Limited ABN 96 067 524 216, AFSL 235030 and available from
                        20 August 2025. The experience provides general
                        information only and does not consider your personal
                        objectives, financial situation, or needs.
                        <br />
                        <br />
                        Before deciding whether to acquire or continue to hold
                        this product, or to understand if this product is right
                        for you, please read the Product Disclosure Statement
                        (PDS) and Target Market Determination (TMD) available at&nbsp; 
                        <a href="https://www.commbank.com.au/insurance/brochures-forms.html" target="_blank" className="underline break-all"> 
                          commbank.com.au/insurance/brochures-forms.html
                        </a> from
                        20 August 2025.
                      </p>
                    </div>
                    <div className="absolute bottom-0 left-0 right-2 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none"></div>
                  </div>
                  <button
                    className="mx-auto relative max-w-[210px] w-full h-[50px] border text-[#3A3A3A] text-base font-cba-sans-extrabold border-black rounded-full active:opacity-70 flex-shrink-0 mt-4"
                    onClick={() => setHasAcknowledged(true)}
                  >
                    Acknowledge
                  </button>
                </div>
              </Modal>
            </ModalOverlay>
          ) : (
            <OnboardingModals
              onClose={handleOnboardingClose}
              firstSlideOnly={showFirstSlideOnboardingOnly}
            />
          )}
        </div>
      </FadeTransition>

      <FadeTransition
        show={
          appState === AppState.ARTWORK_VIEWING &&
          artworkState === ArtworkState.PLACING &&
          !atHome
        }
      >
        <div className="">
          <div className="h-full w-full fixed inset-0 z-20">
            <ImageTrackingOverlay />
          </div>
        </div>
      </FadeTransition>

      <FadeTransition
        show={
          appState === AppState.ARTWORK_VIEWING &&
          artworkState === ArtworkState.PLACING &&
          atHome
        }
      >
        <div className="">
          <div className="h-full w-full fixed inset-0 z-20">
            <TrackingOverlay onStatusNormal={handleOnStatusNormal} />
          </div>
        </div>
      </FadeTransition>

      <FadeTransition
        show={
          appState === AppState.ARTWORK_VIEWING &&
          artworkState === ArtworkState.LOADING &&
          delayedLoading
        }
      >
        <div className="bg-black bg-opacity-75 h-full w-full fixed inset-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2">
            <Spinner />
          </div>
        </div>
      </FadeTransition>
      <FadeTransition show={appState === AppState.ARTWORK_VIEWING}>
        <div className="h-full w-full relative">
          <div
            className={clsx(
              "fixed bottom-0 left-0 w-screen h-auto overflow-auto transition-all duration-500 min-h-[250px] md:min-h-[300px] z-40",
              {
                "translate-y-[57px]":
                  uiState === UIContentState.MAIN_MENU ||
                  cubeInfoState === CubeInfoState.BACK ||
                  artworkState === ArtworkState.PLACING,
                "translate-y-0": uiState === UIContentState.STORY_CONTENT,
                "translate-y-[300px]":
                  uiState === UIContentState.NONE ||
                  artworkState === ArtworkState.LOADING,
              }
            )}
          >
            <img
              src={BottomBar}
              alt="cba background"
              className="absolute bottom-0 left-0 w-screen h-full xs:h-auto object-contain object-bottom animate-slide-up"
            />
          </div>

          {artworkState === ArtworkState.PLACING && (
            <div className="opacity-0 animate-delayed-fade-in z-40 absolute bottom-0 left-0 w-full">
              {atHome && (
                <h2
                  className="p-[30px] w-full font-cba-sans-bold text-2xl md:text-[28px] leading-[28px] md:leading-[31px]"
                  dangerouslySetInnerHTML={{ __html: placegroundMessage }}
                ></h2>
              )}

              {!atHome && (
                <h2 className="p-[30px] w-full font-cba-sans-bold text-2xl leading-[28px] md:text-[28px] md:leading-[31px]">
                  Point your <br />
                  device at the <br /> cube to activate it.
                </h2>
              )}
            </div>
          )}

          {artworkState === ArtworkState.VIEWING && (
            <div className="absolute left-0 bottom-0 z-40 h-full w-full flex items-end justify-start gap-[38px] px-[30px] pb-[25px] xs:pb-[35px]">
              {cubeInfoState === CubeInfoState.INFO &&
                uiState === UIContentState.STORY_CONTENT && (
                  <div className="flex flex-col gap-6 opacity-0 animate-delayed-fade-in">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      className="pointer-events-auto cursor-pointer"
                      onClick={() => setCubeInfoState(CubeInfoState.BACK)}
                    >
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0 19.2308L19.1789 0L19.9461 0.769231L0.767157 20L0 19.2308Z"
                        fill="black"
                      />
                      <path
                        fill-rule="evenodd"
                        clip-rule="evenodd"
                        d="M0.767161 3.9134e-07L19.9461 19.2308L19.1789 20L5.00103e-06 0.76923L0.767161 3.9134e-07Z"
                        fill="black"
                      />
                    </svg>
                    {cubeType === CubeType.CAR && (
                      <p className="text-[15px] xs:text-[17px] md:!text-[21px] pr-0 xs:pr-8 leading-tight">
                        Car Insurance with the optional service of <br />
                        Hollard Roadside Assistance including minor <br />
                        repairs or towing to get you moving again. <br />
                        <br />
                        <span className="font-cba-sans-bold font-[18px]">
                          Car Insurance provided by Hollard. <br />
                          That’s clever cover.
                        </span>
                      </p>
                    )}
                    {cubeType === CubeType.HOME && (
                      <p className="text-[15px] xs:text-[17px] md:!text-[21px] pr-0 xs:pr-8 leading-tight">
                        You could reduce the stress with up to 12 <br />
                        months temporary accommodation if your <br />
                        home is unliveable after an event. <br />
                        <br />
                        <span className="font-cba-sans-bold font-[18px]">
                          Home Insurance provided by Hollard.
                          <br />
                          That’s clever cover.
                        </span>
                      </p>
                    )}
                    {cubeType === CubeType.LANDLORD && (
                      <p className="text-[15px] xs:text-[17px] md:!text-[21px] pr-0 xs:pr-8 leading-tight">
                        Landlord Insurance with optional cover pays <br />
                        for loss or damage caused by malicious acts <br />
                        and theft by tenants. <br />
                        <br />
                        <span className="font-cba-sans-bold font-[18px]">
                          Landlord Insurance provided by Hollard. <br />
                          That’s clever cover.
                        </span>
                      </p>
                    )}
                  </div>
                )}
              {cubeInfoState === CubeInfoState.SELECT &&
                uiState === UIContentState.MAIN_MENU && (
                  <div className="opacity-0 animate-delayed-fade-in flex items-center justify-start gap-6">
                    <button
                      className="pointer-events-auto flex items-center justify-center flex-col font-cba-sans-bold xs:text-[17px] text-[15px] gap-1"
                      onClick={() => handleCubeSelect(0)}
                    >
                      <CubeSvg className="max-w-[50px] xs:max-w-[55px] w-full" />
                      <p className=" ">Car</p>
                    </button>
                    <button
                      className="flex items-center justify-center flex-col font-cba-sans-bold xs:text-[17px] text-[15px] gap-1"
                      onClick={() => handleCubeSelect(1)}
                    >
                      <CubeSvg className="max-w-[50px] xs:max-w-[55px] w-full" />
                      <p className="">Home</p>
                    </button>
                    <button
                      className="flex items-center justify-center flex-col font-cba-sans-bold xs:text-[17px] text-[15px] gap-1"
                      onClick={() => handleCubeSelect(2)}
                    >
                      <CubeSvg className="max-w-[50px] xs:max-w-[55px] w-full" />
                      <p className="">Landlord</p>
                    </button>
                  </div>
                )}
              {cubeInfoState === CubeInfoState.BACK && (
                <button
                  className="animate-fade-in flex items-center justify-center -ml-[12px] flex-col font-cba-sans-bold text-[17px] gap-1"
                  onClick={handleBackSelect}
                >
                  <svg
                    width="67"
                    height="61"
                    viewBox="0 0 67 61"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M23 33.5H1" stroke="black" />
                    <path d="M6.5 28L1 33.5L6.5 39" stroke="black" />
                    <path
                      d="M13 12.9578L39.544 1.00049L65.9838 12.9578L64.8388 45.3065L39.544 60.0396L14.145 45.3065L13 12.9578Z"
                      stroke="black"
                      stroke-width="1.25141"
                    />
                    <path
                      d="M13.1484 12.9575L39.6924 1.00024L66.1323 12.9575M13.1484 12.9575L14.2935 45.3063L39.6924 60.0394M13.1484 12.9575L39.6924 26.5162M39.6924 60.0394L64.9872 45.3063L66.1323 12.9575M39.6924 60.0394V26.5162M66.1323 12.9575L39.6924 26.5162"
                      stroke="black"
                      stroke-opacity="0.2"
                      stroke-width="1.25141"
                    />
                  </svg>

                  <p className="">Back</p>
                </button>
              )}
            </div>
          )}
          <InfoButton
            onClick={handleInfoOpen}
            className="fixed right-[24px] top-[24px] pointer-events-auto z-40"
          />
          {atHome && (
            <RepositionButton
              onClick={() => {
                renderer?.repositionArtwork();
                renderer?.showReticle();
              }}
              className="fixed right-[24px] top-[94px] pointer-events-auto z-40 "
            />
          )}
        </div>
      </FadeTransition>

      <FadeTransition
        show={
          noAR &&
          !atHome &&
          appState == AppState.ARTWORK_VIEWING &&
          artworkState === ArtworkState.PLACING
        }
      >
        <div className="xw-full xh-full">
          <button
            id="activate-cube"
            className="absolute top-10 left-1/2 -translate-x-1/2 z-[1000] bg-white max-w-[210px] w-full h-[50px] border text-[#3A3A3A] text-base font-cba-sans-extrabold border-black rounded-full active:opacity-70 "
            onClick={() => {
              renderer?.activateCube();

              // handleLoadArtwork(artworkData[1].id);
            }}
          >
            Activate cube
          </button>
        </div>
      </FadeTransition>
    </div>
  );
}
